(ns client-portal.client.ai-agent.report-history.views
  "The builder's History view — the saved report's versions, newest first — and the
   dialog confirming a restore. The picked version's preview renders in
   client.ai-agent.views, which can depend on client.reports."
  (:require [client-portal.client.ai-agent.report-history.data]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.components.components :as c]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.time :as time]
            [shared.core.util :as util]))

(defn version-saved-label [{:keys [inserted-at modified-by-email-address]}]
  (->> [(time/relative-time-label inserted-at)
        (some->> modified-by-email-address (not-empty) (str "Saved by "))]
       (remove s/blank?)
       (s/join " · ")))

(defn- version-chip [label style]
  [:span {:style (merge {:border-radius 10
                         :font-size 11
                         :font-weight 500
                         :line-height 1.45
                         :padding "1px 8px"}
                        style)}
   label])

(defn- version-row [{:keys [version title current? editing? home? active?] :as entry}]
  (let [single-pane? @(re/subscribe [:layout/single-pane?-])
        active-background (when active?
                            (style/alc-color "Background/active_secondary"))]
    [c/interactive
     :div {:style {:background-color (or active-background
                                         (style/alc-color "Background/primary"))
                   :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                   :border-radius 6
                   :cursor :pointer
                   :display :flex
                   :flex "0 0 auto"
                   :flex-direction :column
                   :gap 4
                   :padding "10px 12px"
                   :transition "0.2s"
                   :width "100%"}
           :hover-style {:background-color (or active-background
                                               (style/alc-color "Background/hover"))}
           :on-click (fn []
                       (re/dispatch [:ai-agent/select-report-version (when-not home? version)])
                       (when single-pane?
                         (re/dispatch [:ai-agent/set-builder-chat-open false])))}
     [:div {:style {:display :flex
                    :align-items :center
                    :gap 8}}
      [:span {:style {:color (style/alc-color "Text/primary_header")
                      :font-size 14
                      :font-weight 500
                      :line-height 1.43}}
       (str "Version " version)]
      (when current?
        [version-chip "Current" {:background-color (style/alc-color "Common/active")
                                 :color (style/alc-color "Common/White")}])
      (when editing?
        [version-chip "Editing" {:background-color (style/alc-color "Background/primary")
                                 :border (str "1px solid " (style/alc-color "Border/divider"))
                                 :color (style/alc-color "Text/primary")}])]
     [:span {:style {:color (style/alc-color "Text/primary")
                     :display "-webkit-box"
                     :-webkit-line-clamp 2
                     :-webkit-box-orient "vertical"
                     :overflow :hidden
                     :font-size 13
                     :line-height 1.43}}
      title]
     [:span {:style {:color (style/alc-color "Text/secondary")
                     :font-size 12
                     :line-height 1.33
                     :overflow :hidden
                     :text-overflow :ellipsis
                     :white-space :nowrap}}
      (version-saved-label entry)]]))

(defn- history-list []
  (let [versions- (re/subscribe [:ai-agent/report-history-])
        previewed- (re/subscribe [:ai-agent/previewed-report-version-])
        editing-version- (re/subscribe [:ai-agent/editing-report-version-])
        newer-saved?- (re/subscribe [:ai-agent/newer-report-version-saved?-])
        refreshing?- (re/subscribe [:ai-agent/report-history-refreshing?-])]
    (re/create-class
      {:component-did-mount #(re/dispatch [:ai-agent/refresh-report-history])
       :reagent-render
       (fn []
         (let [versions @versions-
               previewed-version (:version @previewed-)
               editing-version @editing-version-]
           (cond
             (or (nil? versions) (util/loading? versions) @refreshing?-)
             [:div {:style {:display :flex
                            :justify-content :center
                            :padding "60px 0"}}
              [alc/up-loader-with-label {:label "Loading History..."}]]

             (:error? versions)
             [:div {:style {:display :flex
                            :flex-direction :column
                            :align-items :center
                            :gap 12
                            :padding "40px 0"}}
              [:span {:style {:color (style/alc-color "Text/primary")
                              :font-size 13}}
               "Failed to load the report's history."]
              [alc/up-button {:on-click #(re/dispatch [:ai-agent/refresh-report-history])}
               "Try Again"]]

             (empty? versions)
             [empty-state/no-data-card {:material-icon-type :history
                                        :text "No Saved Versions"
                                        :style {:padding "24px 12px"}}]

             :else
             (into [:div {:style {:display :flex
                                  :flex-direction :column
                                  :gap 8}}]
                   (map-indexed (fn [idx {:keys [version] :as entry}]
                                  (let [home? (= version editing-version)]
                                    ^{:key version}
                                    [version-row (assoc entry
                                                   :current? (zero? idx)
                                                   :editing? (and home? @newer-saved?-)
                                                   :home? home?
                                                   :active? (if previewed-version
                                                              (= version previewed-version)
                                                              home?))]))
                                versions)))))})))

(defn newer-version-banner
  "Calls out that the report has been saved since this builder session last saved it
   (from another tab, or by someone else), so the builder is a version behind."
  []
  (let [versions- (re/subscribe [:ai-agent/report-history-])
        editing-version- (re/subscribe [:ai-agent/editing-report-version-])
        single-pane?- (re/subscribe [:layout/single-pane?-])]
    (fn []
      (let [versions @versions-
            latest (when (vector? versions)
                     (first versions))
            editing-version @editing-version-
            warning-color (style/alc-color "Chart/orange")]
        [:div {:style {:align-items :center
                       :background-color (util/hex->rgba warning-color 0.12)
                       :border (str "1px solid " (util/hex->rgba warning-color 0.5))
                       :border-radius 8
                       :display :flex
                       :flex "0 0 auto"
                       :flex-wrap :wrap
                       :gap 10
                       :margin-top 10
                       :margin-right (when @single-pane?- 44)
                       :padding "10px 12px"}}
         [:div {:style {:align-items :center
                        :display :flex
                        :flex "1 1 240px"
                        :gap 10
                        :min-width 0}}
          [alc/up-icon {:icon-type :WarningIcon
                        :style {:color warning-color}}]
          [:div {:style {:display :flex
                         :flex-direction :column
                         :gap 2
                         :min-width 0}}
           [:span {:style {:color (style/alc-color "Text/primary_header")
                           :font-size 14
                           :font-weight 500
                           :line-height 1.43}}
            (if editing-version
              (str "You're editing Version " editing-version " — a newer version has been saved")
              "A newer version of this report has been saved")]
           (when latest
             [:span {:style {:color (style/alc-color "Text/secondary")
                             :font-size 12
                             :line-height 1.33}}
              (str "Version " (:version latest) " · " (version-saved-label latest))])]]
         [:div {:style {:flex "0 0 auto"
                        :display :flex
                        :align-items :center
                        :gap 8}}
          [alc/up-button {:on-click #(re/dispatch [:ai-agent/view-latest-report-version])
                          :disabled (nil? latest)}
           "View Latest"]
          [alc/up-button {:on-click #(re/dispatch [:ai-agent/load-latest-report])
                          :tooltip-content "Start a new edit from the latest version. Unsaved changes here are discarded."}
           "Load Latest"]]]))))

(defn history-content []
  (let [report-id- (re/subscribe [:ai-agent/builder-report-public-id-])]
    (fn []
      [:div {:style {:padding "16px 10px 30px"}}
       (if (empty? @report-id-)
         [:div {:style {:display :flex
                        :flex-direction :column
                        :align-items :center
                        :gap 8}}
          [empty-state/no-data-card {:material-icon-type :history
                                     :text "No Saved Versions"
                                     :style {:padding "24px 12px 0"}}]
          [:span {:style {:color (style/alc-color "Text/secondary")
                          :font-size 13
                          :line-height 1.4
                          :text-align :center}}
           "Save the report to start its version history."]]
         [history-list])])))

(defmethod alc/dialog-view
  :report/restore-version-dialog [_ _]
  (let [{:keys [version title] :as entry} @(re/subscribe [:ai-agent/previewed-report-version-])
        {:keys [last-modified-by-email-address last-modified-at] :as conflict} @(re/subscribe [:ai-agent/restore-report-version-conflict-])
        restoring? @(re/subscribe [:ai-agent/restoring-report-version?-])
        mobile? @(re/subscribe [:core/mobile-layout?-])
        secondary-style {:color (style/alc-color "Text/secondary")
                         :font-size 12
                         :line-height 1.33}]
    [alc/dialog {:title (if conflict
                          "This Report Has Changed"
                          (str "Restore Version " version "?"))
                 :on-close #(re/dispatch [:ai-agent/close-restore-report-version])
                 :footer [:<>
                          [alc/up-call-to-action-button {:on-click #(if conflict
                                                                      (re/dispatch [:ai-agent/restore-report-version :overwrite])
                                                                      (re/dispatch [:ai-agent/restore-report-version]))
                                                         :disabled (nil? version)
                                                         :loading? restoring?}
                           (if conflict "Restore Anyway" "Restore")]
                          [alc/up-button {:on-click #(re/dispatch [:ai-agent/close-restore-report-version])
                                          :disabled restoring?}
                           "Cancel"]]}
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 12
                    :width (if mobile? "100%" 480)}}
      (if conflict
        [:<>
         [:span {:style {:color (style/alc-color "Text/primary")
                         :font-size 14
                         :line-height 1.43}}
          (str "A newer version of this report was saved after you started editing it. Restoring version "
               version
               " replaces it as the current version.")]
         (when (not-empty last-modified-by-email-address)
           [:span {:style secondary-style}
            (str "Last saved by " last-modified-by-email-address
                 (when-let [ago (not-empty (time/relative-time-label last-modified-at))]
                   (str " · " ago)))])]
        [:<>
         [:span {:style {:color (style/alc-color "Text/primary")
                         :font-size 14
                         :line-height 1.43}}
          (str "\"" title "\" will be saved as the newest version of this report, and the builder will continue from it.")]
         (when entry
           [:span {:style secondary-style}
            (str "Version " version " · " (version-saved-label entry))])])
      [:span {:style secondary-style}
       "Every version, including the current one, stays in the report's history. Report settings and sharing are not changed."]]]))
